<template>
  <div
    v-if="formattedBlock"
    class="mega-menu-quick-visual-links"
    :style="{ '--quick-link-blocks-size': imageCount }"
  >
    <div
      class="mega-menu-quick-visual-links__block"
      role="region"
      :aria-labelledby="handleisedTitle"
    >
      <h3
        v-if="formattedBlock.megamenuImagesTitle"
        :id="handleisedTitle"
        class="desktop-mega-menu__list-title"
      >
        <a
          v-if="formattedBlock.megamenuImagesTitleUrl"
          :href="formattedBlock.megamenuImagesTitleUrl"
          class="link desktop-mega-menu__list-link"
        >
          {{ formattedBlock.megamenuImagesTitle }}
        </a>
        <span v-else>{{ formattedBlock.megamenuImagesTitle }}</span>
      </h3>

      <ul class="mega-menu-quick-visual-links__list">
        <li
          v-for="(image, index) in formattedBlock.images"
          :key="index"
          class="mega-menu-quick-visual-links__item"
        >
          <component
            :is="image.bannerLink ? 'a' : 'div'"
            v-if="image.bannerImage"
            :href="image.bannerLink"
            class="mega-menu-quick-visual-links__media"
            :class="{
              'mega-menu-quick-visual-links__media--hover':
                formattedBlock.enableHoverEffect && image.bannerLink,
            }"
            :aria-labelledby="image.bannerTitle ? `link-title-${index}` : null"
          >
            <responsive-image
              :image="image.bannerImage"
              :alt="image.bannerImage.alt"
              class="mega-menu-quick-visual-links__image"
            />
            <div
              class="mega-menu-quick-visual-links__image-overlay"
              :style="{ opacity: image.bannerOverlayOpacity }"
            />
          </component>

          <h3
            v-if="image.bannerTitle"
            :id="`link-title-${index}`"
            class="mega-menu-quick-visual-links__title"
          >
            <a
              v-if="image.bannerLink"
              :href="image.bannerLink"
              class="mega-menu-quick-visual-links__title-link"
            >
              {{ image.bannerTitle }}
            </a>
            <span v-else>{{ image.bannerTitle }}</span>
          </h3>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

/**
 * Component: Mega Menu Quick Visual Links
 * -----------------------------------------------------------------------------
 * - Displays a visual block of quick-access image links inside a mega menu,
 * adjusted to the current device viewport.
 * @param {object} quickLinkBlock - Formatted quick link block.
 */

import breakpoints from '~/helpers/breakpoints'
import ResponsiveImage from '~components/responsive-image/responsive-image'

import { stringToHandle } from '~/helpers/strings'

export default {
  name: 'MegaMenuQuickVisualLinks',

  components: {
    ResponsiveImage,
  },

  props: {
    quickLinkBlock: {
      type: [ Boolean, Object ],
      required: true,
    },
  },

  computed: {

    /**
     * Returns the current block trimmed for the current device view.
     * @returns {object | null}
     */
    formattedBlock() {
      if (!this.quickLinkBlock) {
        return null
      }

      const { mobileView, tabletView } = this.getDeviceView()
      const limit = mobileView ? 2 : tabletView ? 3 : 4

      return {
        ...this.quickLinkBlock,
        images: (this.quickLinkBlock.images || []).slice(0, limit),
      }
    },

    /**
     * Computes the number of visible images for current device.
     * @returns {number}
     */
    imageCount() {
      return this.formattedBlock?.images.length || 0
    },

    /**
     * Calculates handleised title for `aria-*` attributes usage.
     * @returns {string}
     */
    handleisedTitle() {
      return stringToHandle(this.formattedBlock?.megamenuImagesTitle)
    },
  },

  methods: {

    /**
     * Determines the current screen size category.
     * @returns {object}
     */
    getDeviceView() {
      const mobileView = window.matchMedia(
        `(max-width: ${breakpoints.small})`,
      ).matches
      const tabletView =
          window.matchMedia(`(min-width: ${breakpoints.small})`).matches &&
          window.matchMedia(`(max-width: ${breakpoints.x_large})`).matches

      return { mobileView, tabletView }
    },
  },
}
</script>

<style src="./mega-menu-quick-visual-links.scss" lang="scss"></style>
